import { PORTAL_CLASS_NAME } from '@billy/design-system'
import { useEffect } from 'react'
import type { RefObject } from 'react'

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ')

interface UseCloseOnEscapeParams {
  onClose: () => void
  overlayRef: RefObject<HTMLDivElement>
}

interface UseFecImportModalParams {
  onClose: () => void
  overlayRef: RefObject<HTMLDivElement>
}

const isLumenFloatingUiOpen = (): boolean =>
  Boolean(document.querySelector('[role="combobox"][aria-expanded="true"], [role="listbox"]'))

const getFocusableElements = (root: HTMLElement): HTMLElement[] =>
  Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (element) => element.offsetWidth > 0 || element.offsetHeight > 0,
  )

const useCloseOnEscape = ({ onClose, overlayRef }: UseCloseOnEscapeParams): void => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return
      }

      if (isLumenFloatingUiOpen()) {
        return
      }

      const overlayElement = overlayRef.current

      if (!overlayElement) {
        return
      }

      const portalElements = document.querySelectorAll(`.${PORTAL_CLASS_NAME}`)

      if (Array.from(portalElements).at(-1) !== overlayElement) {
        return
      }

      onClose()
    }

    globalThis.addEventListener('keydown', handleKeyDown, true)

    return () => {
      globalThis.removeEventListener('keydown', handleKeyDown, true)
    }
  }, [onClose, overlayRef])
}

const useLockBodyOverflow = (): void => {
  useEffect(() => {
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [])
}

const useFocusTrap = (overlayRef: RefObject<HTMLDivElement>): void => {
  useEffect(() => {
    const overlayElement = overlayRef.current
    const previouslyFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null

    overlayElement?.focus()

    const handleFocusTrapKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || isLumenFloatingUiOpen() || !overlayElement) {
        return
      }

      const focusableElements = getFocusableElements(overlayElement)

      if (focusableElements.length === 0) {
        event.preventDefault()
        overlayElement.focus()
        return
      }

      const firstFocusableElement = focusableElements[0]
      const lastFocusableElement = focusableElements.at(-1)
      const activeElement = document.activeElement

      if (event.shiftKey && (activeElement === firstFocusableElement || activeElement === overlayElement)) {
        event.preventDefault()
        lastFocusableElement?.focus()
        return
      }

      if (!event.shiftKey && lastFocusableElement && activeElement === lastFocusableElement) {
        event.preventDefault()
        firstFocusableElement.focus()
      }
    }

    overlayElement?.addEventListener('keydown', handleFocusTrapKeyDown)

    return () => {
      overlayElement?.removeEventListener('keydown', handleFocusTrapKeyDown)
      previouslyFocusedElement?.focus()
    }
  }, [overlayRef])
}

export const useFecImportModal = ({ onClose, overlayRef }: UseFecImportModalParams): void => {
  useLockBodyOverflow()
  useCloseOnEscape({ onClose, overlayRef })
  useFocusTrap(overlayRef)
}
